import React from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';

import { token } from '@atlaskit/tokens';

import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import ObjectActiveIncidentsPanel from '@atlassian/jira-servicedesk-object-active-incidents-panel/src/ui/active-incident-panel/index.tsx';
import { CompassSmartLink } from '@atlassian/jira-servicedesk-services-components/src/ui/compass-component-field/compass-smart-link/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ANALYTICS_PACKAGE_NAME } from '../../common/constants.tsx';
import type { ServiceDetails, ValueType } from '../../common/types/index.tsx';
import messages from './messages.tsx';

export type Props = {
	isEditing: boolean;
	value: ValueType<ServiceDetails>;
};

const Description = ({ value }: { value: ServiceDetails }) => {
	const { formatMessage } = useIntl();
	const isEmpty = value.description == null || value.description === '';

	return (
		<>
			<DescriptionLabel>{formatMessage(messages.description)}</DescriptionLabel>
			{isEmpty ? (
				<EmptyDescription>{formatMessage(messages.emptyDescription)}</EmptyDescription>
			) : (
				<DescriptionValue>{value.description}</DescriptionValue>
			)}
		</>
	);
};

export const DetailContent = (props: Props): React.JSX.Element | null => {
	const { value } = props;
	const { formatMessage } = useIntl();

	if (value == null) {
		return null;
	}

	let serviceId;
	let level;
	let properties;

	if (fg('fix-tier-nullish-issue')) {
		({
			data: { id: serviceId, tier: { level } = { level: '' }, properties },
		} = value);
	} else {
		({
			data: {
				id: serviceId,
				tier: { level },
				properties,
			},
		} = value);
	}

	return (
		<Box xcss={wrapperStyles}>
			<ObjectActiveIncidentsPanel
				serviceId={serviceId}
				renderWrapper={({ hasError }, children) => (
					<ActiveIncidentsPanelWrapper hasError={hasError}>{children}</ActiveIncidentsPanelWrapper>
				)}
			/>
			<Description value={value.data} />
			<AttributeList>
				<Attribute>
					<Label>{formatMessage(messages.tier)}</Label>
					<Value>
						{formatMessage(messages.tier)} {level}
					</Value>
				</Attribute>
				{!!properties?.compass_component && (
					<JSErrorBoundary
						id="issueServiceEntityDetailContent"
						packageName={ANALYTICS_PACKAGE_NAME}
						// If the link points to a non-existent component, `CompassSmartLink` will blow up - we should not show the "Compass" field.
						// This is a rare edge case as links between JSM and Compass should be automatically cleaned up when a component is deleted.
						fallback="unmount"
					>
						<Attribute>
							<Label>{formatMessage(messages.compass)}</Label>
							<Value>
								<CompassSmartLink url={properties.compass_component.url} />
							</Value>
						</Attribute>
					</JSErrorBoundary>
				)}
			</AttributeList>
		</Box>
	);
};

const ATTRIBUTE_VERTICAL_GAP = 8; // Total vertical space between each attribute
const LABEL_VALUE_HORIZONTAL_GAP = 24; // Total horizontal space between label & value

const DescriptionLabel = (props: { children: React.ReactNode }) => (
	<Heading size="xsmall" as="div">
		<DescriptionLabelComponent {...props} />
	</Heading>
);

const Label = (props: { children: React.ReactNode }) => (
	<LabelHeader>
		<Heading size="xsmall" as="div">
			<LabelComponent {...props} />
		</Heading>
	</LabelHeader>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const LabelHeader = styled.div({
	flexGrow: 1,
	flexBasis: '112px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionValue = styled.div({
	color: token('color.text'),
	margin: `${ATTRIBUTE_VERTICAL_GAP / 2}px ${LABEL_VALUE_HORIZONTAL_GAP / 2}px ${
		ATTRIBUTE_VERTICAL_GAP * 1.5
	}px 0`,
	minHeight: '24px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
	lineHeight: '24px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyDescription = styled.div({
	color: token('color.text.subtlest'),
	margin: `${ATTRIBUTE_VERTICAL_GAP / 2}px ${LABEL_VALUE_HORIZONTAL_GAP / 2}px ${
		ATTRIBUTE_VERTICAL_GAP * 1.5
	}px 0`,
	minHeight: '24px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
	lineHeight: '24px',
});

// // eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const Value = styled.div({
	color: token('color.text'),
	margin: `0 ${LABEL_VALUE_HORIZONTAL_GAP / 2}px`,
	minHeight: '24px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
	lineHeight: '24px',
	flexBasis: '224px',
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const ActiveIncidentsPanelWrapper = styled.div<{ hasError?: boolean }>((props) => ({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundColor: props.hasError
		? token('color.background.danger')
		: token('color.background.warning'),
	margin: `${ATTRIBUTE_VERTICAL_GAP * 1.5}px 0 ${ATTRIBUTE_VERTICAL_GAP / 2}px 0`,
}));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionLabelComponent = styled.div({
	color: token('color.text.subtle'),
	margin: `${ATTRIBUTE_VERTICAL_GAP * 1.5}px ${LABEL_VALUE_HORIZONTAL_GAP / 2}px ${
		ATTRIBUTE_VERTICAL_GAP / 2
	}px 0`,
	minHeight: '24px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
	lineHeight: '24px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelComponent = styled.div({
	color: token('color.text.subtle'),
	margin: `${ATTRIBUTE_VERTICAL_GAP / 2}px ${LABEL_VALUE_HORIZONTAL_GAP / 2}px ${
		ATTRIBUTE_VERTICAL_GAP / 2
	}px 0`,
	minHeight: '24px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
	lineHeight: '24px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AttributeList = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Attribute = styled.div({
	display: 'flex',
	flexDirection: 'row',
});

const wrapperStyles = xcss({
	paddingTop: 'space.200',
});
